import { useTranslation } from "@luckymenu/i18n/react";
import {
	useActiveSwitch,
	useMe,
	useNavigationModel,
	usePlatform,
	useSignOut,
	useSwitchRestaurant,
} from "@luckymenu/staff-core";
import { Button, Card, CardContent, PageHeader } from "@luckymenu/ui-web";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, LogOut } from "lucide-react";

export const Route = createFileRoute("/more/")({ component: More });

/** More entries that have their page already. */
const MORE_ROUTES = {
	staff: "/more/staff",
	bills: "/more/bills",
	restaurantProfile: "/more/profile",
	tablesAndQr: "/more/tables",
	orderingSettings: "/more/ordering",
	billingSettings: "/more/billing",
	subscription: "/more/subscription",
	reports: "/more/reports",
	dayClose: "/more/day-close",
	activityLog: "/more/activity",
	printers: "/more/printers",
	offers: "/more/offers",
	device: "/more/device",
	switchPerson: "/more/switch",
} as const;

function More() {
	const { t } = useTranslation(["nav", "common", "device"]);
	const acting = useActiveSwitch();
	const platform = usePlatform();
	const me = useMe();
	const signOut = useSignOut();
	const switchRestaurant = useSwitchRestaurant();
	const items = (useNavigationModel()?.more ?? []).filter((m) => m.key !== "signOut");
	return (
		<>
			<PageHeader
				title={t("more")}
				description={
					acting
						? t("device:switch.workingAs", { name: acting.name ?? t("device:switch.unnamed") })
						: (me.data?.email ?? undefined)
				}
			/>
			<Card className="max-w-lg py-2">
				<CardContent className="px-2">
					<ul>
						{items.map((item) =>
							item.key === "switchRestaurant" ? (
								<li key={item.key}>
									<button
										type="button"
										onClick={() => void switchRestaurant(null)}
										className="flex min-h-12 w-full items-center justify-between rounded-button px-3 text-left text-sm hover:bg-muted"
									>
										{t("switchRestaurant")}
										<ChevronRight className="size-4 text-muted-foreground" aria-hidden />
									</button>
								</li>
							) : item.key in MORE_ROUTES ? (
								<li key={item.key}>
									<Link
										to={MORE_ROUTES[item.key as keyof typeof MORE_ROUTES]}
										className="flex min-h-12 items-center justify-between rounded-button px-3 text-sm hover:bg-muted"
									>
										{t(item.labelKey as "staff")}
										<ChevronRight className="size-4 text-muted-foreground" aria-hidden />
									</Link>
								</li>
							) : (
								<li
									key={item.key}
									className="flex min-h-12 items-center justify-between rounded-button px-3 text-sm text-muted-foreground"
								>
									{t(item.labelKey as "reports")}
									<ChevronRight className="size-4" aria-hidden />
								</li>
							),
						)}
					</ul>
				</CardContent>
			</Card>
			<div className="mt-6 flex max-w-lg flex-col gap-3">
				<Button variant="outline" size="lg" onClick={() => void signOut()}>
					<LogOut />
					{t("signOut")}
				</Button>
				<p className="text-center text-xs text-muted-foreground">
					{t("common:appVersion", { version: platform.appVersion })}
				</p>
			</div>
		</>
	);
}
